import { createWebColorPicker } from "./color_picker";

type ButtonVariant = "primary" | "secondary" | "danger";

export type ButtonOptions = {
  text: string;
  variant?: ButtonVariant;
  onClick?: () => void;
};

export type TextInputOptions = {
  label: string;
  value: string;
  placeholder?: string;
  maxLength?: number;
  onInput?: (value: string) => void;
};

export type ColorPickerOptions = {
  label: string;
  value: string;
  onInput?: (value: string) => void;
};

export type SelectOption<T extends string> = {
  value: T;
  label: string;
};

export type SelectOptions<T extends string> = {
  label: string;
  value: T;
  options: SelectOption<T>[];
  onChange?: (value: T) => void;
};

export function createPanel(className?: string): HTMLDivElement {
  const panel = document.createElement("div");
  panel.className = className ? `ui-panel ${className}` : "ui-panel";
  return panel;
}

export function createButton(options: ButtonOptions): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `ui-button ui-button-${options.variant ?? "secondary"}`;
  button.textContent = options.text;

  if (options.onClick) {
    button.addEventListener("click", options.onClick);
  }

  return button;
}

export function createTextInput(options: TextInputOptions): HTMLLabelElement {
  const field = document.createElement("label");
  field.className = "ui-field";

  const label = document.createElement("span");
  label.className = "ui-label";
  label.textContent = options.label;

  const input = document.createElement("input");
  input.className = "ui-input";
  input.type = "text";
  input.value = options.value;
  input.placeholder = options.placeholder ?? "";
  input.maxLength = options.maxLength ?? 32;
  input.autocomplete = "off";
  input.spellcheck = false;

  input.addEventListener("input", () => {
    options.onInput?.(input.value);
  });

  field.append(label, input);
  return field;
}

export function createColorPicker(options: ColorPickerOptions): HTMLElement {
  return createWebColorPicker(options);
}

export function createSelect<T extends string>(options: SelectOptions<T>): HTMLLabelElement {
  const field = document.createElement("label");
  field.className = "ui-field";

  const label = document.createElement("span");
  label.className = "ui-label";
  label.textContent = options.label;

  const select = document.createElement("select");
  select.className = "ui-input";

  for (const option of options.options) {
    const element = document.createElement("option");
    element.value = option.value;
    element.textContent = option.label;

    if (option.value === options.value) {
      element.selected = true;
    }

    select.append(element);
  }

  select.addEventListener("change", () => {
    options.onChange?.(select.value as T);
  });

  field.append(label, select);
  return field;
}